// ═══════════════════════════════════════════════════════════════════════════
// PAGE : PRÉPARER UN COMITÉ — WIZARD INTERACTIF (mai 2026)
// Migration S5.3 du plan TS+Vite (2026-05-10) : .js → .ts strict.
// ═══════════════════════════════════════════════════════════════════════════
//
// Wizard popup multi-étapes :
//   1. Form : périmètre (programme/projet/comité/risques/format)
//   2. (si programme + risques) Matrice du programme dans iframe
//   3. Itère sur chaque projet du périmètre (matrice + filtres)
//   4. POST /meeting-support/finalize avec payload complet → download fichier

interface ProgramRef {
  id: number;
  code?: string | null;
  name: string;
  projects?: Array<{ id: number; code?: string | null; name: string }>;
}

interface ProjectRef {
  id: number;
  code?: string | null;
  name: string;
}

interface CommitteeRef {
  id: number;
  code?: string | null;
  name: string;
}

interface MatrixCapture {
  pngB64: string;
  // Liste des risques significatifs (carte #matrix) en PNG — embarquée telle
  // quelle dans les exports PPTX/DOCX (fidèle à l'écran). '' si aucun risque.
  risksPngB64?: string | null;
  filters: { severities?: number[]; categories?: string[] } | unknown;
}

interface WizardProjectState {
  projectId: number;
  code?: string | null;
  name: string;
  skipped: boolean;
  pngB64: string | null;
  risksPngB64?: string | null;
  filters: unknown;
}

interface StructureRef {
  id: number;
  code: string;
  label: string;
  is_active?: boolean;
}

interface WizardState {
  programId: string;
  projectId: string;
  structureId: string;
  committeeId: string;
  withRisks: boolean;
  fmt: 'pptx' | 'xlsx' | 'docx' | string;
  programMatrix: MatrixCapture | null;
  projects: WizardProjectState[];
}

interface WizardRefs {
  programs: ProgramRef[];
  projects: ProjectRef[];
  committees: CommitteeRef[];
  structures: StructureRef[];
}

interface WizardStepOpts {
  title: string;
  body: string;
  onNext: () => Promise<void>;
  onSkip: () => void;
  onCancel: () => void;
}

declare global {
  interface Window {
    _wizardStep1: typeof _wizardStep1;
    _rewireStep1: typeof _rewireStep1;
    _wizardStep2Program: typeof _wizardStep2Program;
    _wizardStep3Project: typeof _wizardStep3Project;
    _captureMatrixFromIframe: typeof _captureMatrixFromIframe;
    _openWizardStepModal: typeof _openWizardStepModal;
    _confirmCancelWizard: typeof _confirmCancelWizard;
    _wizardFinalize: typeof _wizardFinalize;
    _mwExportPng?: () => Promise<string>;
    _mwRisksExportPng?: () => Promise<string>;
    _mwGetFilters?: () => unknown;
  }
}

pages.meetingSupport = async function () {
  setHeader(t('meetingSupport.title'), '');
  setContent(`<div class="loading">${esc(t('common.loading'))}</div>`);

  let programs: ProgramRef[] = [];
  let projects: ProjectRef[] = [];
  let committees: CommitteeRef[] = [];
  let structures: StructureRef[] = [];
  try {
    const [pr, pj, cm, st] = (await Promise.all([
      api.getPrograms(),
      api.getProjects(),
      api.getCommittees(),
      (api.getStructuresIfAllowed() as Promise<StructureRef[]>).catch(() => []),
    ])) as [ProgramRef[], ProjectRef[], CommitteeRef[], StructureRef[]];
    programs = pr;
    projects = pj;
    committees = cm;
    structures = (st || []).filter((s) => s.is_active !== false);
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : t('common.error');
    setContent(`<div class="error">${esc(msg)}</div>`);
    return;
  }
  if (!committees.length) {
    setContent(`<div class="empty-state" style="padding:40px;text-align:center">
      <h3>${esc(t('meetingSupport.empty.committees'))}</h3>
      <p>${esc(t('meetingSupport.empty.committeesDetail'))}</p>
    </div>`);
    return;
  }

  // Query string optionnelle (cf. CTA "Lancer l'assistant" de l'onglet
  // Préparer un support dans #meeting/:id). Pré-remplit le state et saute
  // l'écran d'intro pour aller directement au step 1.
  const hashQs = (() => {
    const h = window.location.hash || '';
    const qIdx = h.indexOf('?');
    return qIdx >= 0 ? new URLSearchParams(h.slice(qIdx + 1)) : null;
  })();
  const hasPreset = !!(
    hashQs &&
    (hashQs.get('prog') ||
      hashQs.get('proj') ||
      hashQs.get('com') ||
      hashQs.get('struct') ||
      hashQs.get('risks'))
  );

  const state: WizardState = {
    programId: hashQs?.get('prog') || '',
    projectId: hashQs?.get('proj') || '',
    structureId: hashQs?.get('struct') || '',
    committeeId: hashQs?.get('com') || String(committees[0]?.id ?? ''),
    withRisks: hashQs?.get('risks') === '1',
    fmt: 'pptx',
    programMatrix: null,
    projects: [],
  };

  if (hasPreset) {
    // Saute l'écran d'intro et entre directement dans le wizard step 1
    // avec les champs déjà positionnés depuis le contexte de la réunion.
    _wizardStep1(state, { programs, projects, committees, structures });
    return;
  }

  setContent(`
    <div style="max-width:700px;margin:24px auto;text-align:center">
      <h2 style="margin-bottom:12px">${esc(t('meetingSupport.title'))}</h2>
      <p style="color:var(--text3);font-size:14px;line-height:1.6;margin-bottom:24px">
        ${esc(t('meetingSupport.intro'))}
      </p>
      <button class="btn btn-primary" id="ms-launch">${esc(t('meetingSupport.btn.launch'))}</button>
    </div>
  `);

  document
    .getElementById('ms-launch')
    ?.addEventListener('click', () =>
      _wizardStep1(state, { programs, projects, committees, structures }),
    );
};

function _wizardStep1(state: WizardState, refs: WizardRefs): void {
  const { programs, projects, committees, structures } = refs;

  const projectOpts = (programId: string): string => {
    const list = programId
      ? programs.find((p) => String(p.id) === String(programId))?.projects || []
      : projects;
    return list
      .map(
        (p) =>
          `<option value="${p.id}"${String(p.id) === state.projectId ? ' selected' : ''}>${esc(p.code ? p.code + ' — ' : '')}${esc(p.name)}</option>`,
      )
      .join('');
  };

  const renderBody = (): string => {
    const programOpts = programs
      .map(
        (p) =>
          `<option value="${p.id}"${String(p.id) === state.programId ? ' selected' : ''}>${esc(p.code ? p.code + ' — ' : '')}${esc(p.name)}</option>`,
      )
      .join('');
    const committeeOpts = committees
      .slice()
      .sort((a, b) => (a.code || '').localeCompare(b.code || ''))
      .map(
        (c) =>
          `<option value="${c.id}"${String(c.id) === state.committeeId ? ' selected' : ''}>${esc(c.code ? c.code + ' — ' : '')}${esc(c.name)}</option>`,
      )
      .join('');
    const structureOpts = structures
      .slice()
      .sort((a, b) => a.code.localeCompare(b.code))
      .map(
        (s) =>
          `<option value="${s.id}"${String(s.id) === state.structureId ? ' selected' : ''}>${esc(s.code)} — ${esc(s.label)}</option>`,
      )
      .join('');
    const fmtOpts = [
      ['xlsx', t('meetingSupport.fmt.xlsx')],
      ['pptx', t('meetingSupport.fmt.pptx')],
      ['docx', t('meetingSupport.fmt.docx')],
    ]
      .map(
        ([k, lbl]) =>
          `<option value="${k}"${k === state.fmt ? ' selected' : ''}>${esc(lbl ?? '')}</option>`,
      )
      .join('');

    return `
      <div style="font-size:12px;color:var(--text3);margin-bottom:14px">
        ${esc(t('meetingSupport.step1.subtitle'))}
      </div>
      <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:14px;margin-bottom:14px">
        <div>
          <label style="font-size:12px;font-weight:500;display:block;margin-bottom:4px">${esc(t('meetingSupport.step1.program'))}</label>
          <select id="ms-program" style="width:100%;font-size:13px;padding:6px;border:1px solid var(--border2);border-radius:var(--radius)">
            <option value=""${state.programId === '' ? ' selected' : ''}>${esc(t('common.none'))}</option>
            ${programOpts}
          </select>
        </div>
        <div>
          <label style="font-size:12px;font-weight:500;display:block;margin-bottom:4px">${esc(t('meetingSupport.step1.project'))}</label>
          <select id="ms-project" style="width:100%;font-size:13px;padding:6px;border:1px solid var(--border2);border-radius:var(--radius)">
            <option value=""${state.projectId === '' ? ' selected' : ''}>${esc(t('common.none'))}</option>
            ${projectOpts(state.programId)}
          </select>
        </div>
        <div>
          <label style="font-size:12px;font-weight:500;display:block;margin-bottom:4px">${esc(t('meetingSupport.step1.structure'))}</label>
          <select id="ms-structure" style="width:100%;font-size:13px;padding:6px;border:1px solid var(--border2);border-radius:var(--radius)">
            <option value=""${state.structureId === '' ? ' selected' : ''}>${esc(t('common.none'))}</option>
            ${structureOpts}
          </select>
        </div>
      </div>
      <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:14px">
        <div>
          <label style="font-size:12px;font-weight:500;display:block;margin-bottom:4px">${esc(t('meetingSupport.step1.committee'))}</label>
          <select id="ms-committee" style="width:100%;font-size:13px;padding:6px;border:1px solid var(--border2);border-radius:var(--radius)">
            ${committeeOpts}
          </select>
        </div>
        <div>
          <label style="font-size:12px;font-weight:500;display:block;margin-bottom:4px">${esc(t('meetingSupport.step1.risks'))}</label>
          <label style="display:flex;align-items:center;gap:8px;font-size:13px;padding:6px 0;cursor:pointer">
            <input type="checkbox" id="ms-risks"${state.withRisks ? ' checked' : ''}>
            ${esc(t('meetingSupport.step1.risksLabel'))}
          </label>
        </div>
        <div>
          <label style="font-size:12px;font-weight:500;display:block;margin-bottom:4px">${esc(t('meetingSupport.step1.format'))}</label>
          <select id="ms-fmt" style="width:100%;font-size:13px;padding:6px;border:1px solid var(--border2);border-radius:var(--radius)">
            ${fmtOpts}
          </select>
        </div>
      </div>
    `;
  };

  openWideModal(
    t('meetingSupport.step1.title'),
    renderBody(),
    async () => {
      if (!state.programId && !state.projectId && !state.structureId) {
        toast(t('meetingSupport.step1.errProg'), 'error');
        return;
      }
      if (state.programId && state.projectId) {
        const prog = programs.find((p) => String(p.id) === state.programId);
        const inProg = (prog?.projects || []).some((p) => String(p.id) === state.projectId);
        if (!inProg) {
          toast(t('meetingSupport.step1.errInProg'), 'error');
          return;
        }
      }
      if (!state.committeeId) {
        toast(t('meetingSupport.step1.errCommittee'), 'error');
        return;
      }
      const projectList: Array<{ id: number; code?: string | null; name: string }> =
        state.programId && !state.projectId
          ? programs.find((p) => String(p.id) === state.programId)?.projects || []
          : state.projectId
            ? [
                projects.find((p) => String(p.id) === state.projectId) ?? {
                  id: 0,
                  code: null,
                  name: '?',
                },
              ]
            : [];
      state.projects = projectList.map((p) => ({
        projectId: p.id,
        code: p.code ?? null,
        name: p.name,
        skipped: false,
        pngB64: null,
        filters: null,
      }));
      if (state.programId && state.withRisks) {
        _wizardStep2Program(state);
      } else {
        _wizardStep3Project(state, 0);
      }
    },
    t('common.next'),
  );

  setTimeout(() => {
    document.getElementById('ms-program')?.addEventListener('change', (e: Event) => {
      const target = e.target as HTMLSelectElement | null;
      state.programId = target?.value || '';
      if (state.programId && state.projectId) {
        const inProg = (
          programs.find((p) => String(p.id) === state.programId)?.projects || []
        ).some((p) => String(p.id) === state.projectId);
        if (!inProg) state.projectId = '';
      }
      const body = document.getElementById('modal-body');

      if (body) body.innerHTML = renderBody();
      _rewireStep1(state);
    });
    _rewireStep1(state);
  }, 0);
}

function _rewireStep1(state: WizardState): void {
  document.getElementById('ms-project')?.addEventListener('change', (e: Event) => {
    const target = e.target as HTMLSelectElement | null;
    state.projectId = target?.value || '';
  });
  document.getElementById('ms-structure')?.addEventListener('change', (e: Event) => {
    const target = e.target as HTMLSelectElement | null;
    state.structureId = target?.value || '';
  });
  document.getElementById('ms-committee')?.addEventListener('change', (e: Event) => {
    const target = e.target as HTMLSelectElement | null;
    state.committeeId = target?.value || '';
  });
  document.getElementById('ms-risks')?.addEventListener('change', (e: Event) => {
    const target = e.target as HTMLInputElement | null;
    state.withRisks = !!target?.checked;
  });
  document.getElementById('ms-fmt')?.addEventListener('change', (e: Event) => {
    const target = e.target as HTMLSelectElement | null;
    state.fmt = target?.value || 'pptx';
  });
}

function _wizardStep2Program(state: WizardState): void {
  const body = `
    <div style="font-size:12px;color:var(--text3);margin-bottom:10px">
      ${esc(t('meetingSupport.step2.subtitle'))}
    </div>
    <div style="flex:1;min-height:480px;border:1px solid var(--border);border-radius:var(--radius);overflow:hidden">
      <iframe id="mw-iframe" src="/#matrix/program/${state.programId}"
        style="width:100%;height:100%;border:0;display:block" loading="lazy"></iframe>
    </div>
  `;
  _openWizardStepModal({
    title: t('meetingSupport.step2.title'),
    body,
    onNext: async () => {
      const result = await _captureMatrixFromIframe();
      // Matrice programme vide (pas de risque escaladé) → on continue sans matrice.
      if (result === 'empty') {
        state.programMatrix = null;
        _wizardStep3Project(state, 0);
        return;
      }
      if (!result) {
        toast(t('meetingSupport.captureError'), 'error');
        return;
      }
      state.programMatrix = result;
      _wizardStep3Project(state, 0);
    },
    onSkip: () => {
      state.programMatrix = null;
      _wizardStep3Project(state, 0);
    },
    onCancel: () => void _confirmCancelWizard(),
  });
}

function _wizardStep3Project(state: WizardState, idx: number): void {
  if (!state.withRisks) {
    void _wizardFinalize(state);
    return;
  }
  if (idx >= state.projects.length) {
    void _wizardFinalize(state);
    return;
  }
  const proj = state.projects[idx];
  if (!proj) return;
  const total = state.projects.length;
  const body = `
    <div style="font-size:12px;color:var(--text3);margin-bottom:10px">
      ${t('meetingSupport.step3.subtitle', { idx: idx + 1, total, label: proj.code || proj.name })}
    </div>
    <div style="flex:1;min-height:480px;border:1px solid var(--border);border-radius:var(--radius);overflow:hidden">
      <iframe id="mw-iframe" src="/#matrix/${proj.projectId}"
        style="width:100%;height:100%;border:0;display:block" loading="lazy"></iframe>
    </div>
  `;
  _openWizardStepModal({
    title: t('meetingSupport.step3.title', { n: idx + 1 }),
    body,
    onNext: async () => {
      const result = await _captureMatrixFromIframe();
      // Matrice projet vide → on saute ce projet (pas de matrice) et on continue.
      if (result === 'empty') {
        proj.skipped = true;
        _wizardStep3Project(state, idx + 1);
        return;
      }
      if (!result) {
        toast(t('meetingSupport.captureError'), 'error');
        return;
      }
      proj.skipped = false;
      proj.pngB64 = result.pngB64;
      proj.risksPngB64 = result.risksPngB64 ?? null;
      proj.filters = result.filters;
      _wizardStep3Project(state, idx + 1);
    },
    onSkip: () => {
      proj.skipped = true;
      _wizardStep3Project(state, idx + 1);
    },
    onCancel: () => void _confirmCancelWizard(),
  });
}

async function _captureMatrixFromIframe(): Promise<MatrixCapture | 'empty' | null> {
  const iframe = document.getElementById('mw-iframe') as HTMLIFrameElement | null;
  if (!iframe || !iframe.contentWindow) return null;
  try {
    const cw = iframe.contentWindow as Window & {
      _mwExportPng?: () => Promise<string>;
      _mwRisksExportPng?: () => Promise<string>;
      _mwGetFilters?: () => unknown;
      _mwEmpty?: boolean;
    };
    // Matrice légitimement vide (aucun risque escaladé / aucun risque) :
    // pas de graphique à capturer, on laisse le wizard poursuivre sans erreur.
    if (cw._mwEmpty) return 'empty';
    const exportFn = cw._mwExportPng;
    const filtersFn = cw._mwGetFilters;
    if (typeof exportFn !== 'function') {
      toast(t('meetingSupport.iframeError'), 'error');
      return null;
    }
    const pngB64 = await exportFn();
    // Liste des risques significatifs (carte) — facultative : '' si aucun.
    const risksPngB64 =
      typeof cw._mwRisksExportPng === 'function' ? await cw._mwRisksExportPng() : '';
    const filters =
      typeof filtersFn === 'function' ? filtersFn() : { severities: [], categories: [] };
    if (!pngB64) return null;
    return { pngB64, risksPngB64, filters };
  } catch (err: unknown) {
    safeWarn('[meeting-wizard] capture iframe failed', err);
    return null;
  }
}

function _openWizardStepModal({ title, body, onNext, onSkip, onCancel }: WizardStepOpts): void {
  closeModal();
  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  overlay.id = 'modal-overlay';
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');
  overlay.style.cssText = 'align-items:flex-start;padding:8px';

  overlay.innerHTML = `
    <div class="modal" style="max-width:98vw;width:98vw;height:96vh;max-height:96vh;display:flex;flex-direction:column;margin:0">
      <div class="modal-header" style="flex-shrink:0">
        <h2 id="modal-title">${esc(title)}</h2>
        <button class="btn btn-secondary btn-sm" id="modal-close" aria-label="${esc(t('common.close'))}">✕</button>
      </div>
      <div class="modal-body" id="modal-body" style="overflow-y:auto;flex:1;display:flex;flex-direction:column">${body}</div>
      <div class="modal-footer" style="flex-shrink:0;display:flex;gap:8px;justify-content:space-between">
        <button class="btn btn-danger" id="mw-cancel">${esc(t('common.cancel'))}</button>
        <div style="display:flex;gap:8px">
          <button class="btn btn-secondary" id="mw-skip">${esc(t('common.skip'))}</button>
          <button class="btn btn-primary" id="mw-next">${esc(t('common.next'))}</button>
        </div>
      </div>
    </div>`;
  document.body.appendChild(overlay);
  // a11y : cet overlay est construit à la main (pas via openModal), il faut
  // donc réinstaller le piège de focus et un focus initial.
  _installFocusTrap(overlay);
  setTimeout(() => document.getElementById('mw-next')?.focus(), 0);
  // ESC : sans ce listener local, le handler ESC global de core.ts fermerait
  // l'overlay via closeModalSafe() en COURT-CIRCUITANT la confirmation
  // d'annulation (onCancel) → perte des captures sans avertissement (bloquant
  // audit UX 2026-06). On intercepte en capture et on stoppe la propagation.
  overlay.addEventListener(
    'keydown',
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        e.preventDefault();
        onCancel();
      }
    },
    true,
  );
  document.getElementById('modal-close')?.addEventListener('click', onCancel);
  document.getElementById('mw-cancel')?.addEventListener('click', onCancel);
  document.getElementById('mw-skip')?.addEventListener('click', () => onSkip());
  document.getElementById('mw-next')?.addEventListener('click', () => {
    const btn = document.getElementById('mw-next') as HTMLButtonElement | null;
    if (!btn) return;
    btn.disabled = true;
    const oldText = btn.textContent;
    btn.textContent = t('meetingSupport.btn.capturing');
    void (async () => {
      try {
        await onNext();
      } finally {
        btn.disabled = false;
        btn.textContent = oldText;
      }
    })();
  });
}

// Si l'utilisateur arrive de l'onglet "Préparer un support" d'une réunion
// (cf. page-meeting.ts), le hash de retour est posé dans sessionStorage
// par le CTA "Lancer l'assistant". Consommé une seule fois.
function _consumeMeetingSupportBackHash(): string | null {
  try {
    const stored = sessionStorage.getItem('meetingSupport:backHash');
    if (stored && stored.startsWith('#')) {
      sessionStorage.removeItem('meetingSupport:backHash');
      return stored;
    }
  } catch {
    /* noop */
  }
  return null;
}

async function _confirmCancelWizard(): Promise<void> {
  const ok = await confirmStackModal({
    message: t('meetingSupport.confirmCancel'),
    danger: true,
    confirmLabel: t('common.yes'),
  });
  if (ok) {
    closeModal();
    toast(t('meetingSupport.toast.cancelled'), 'info');
    // Cancel = retour à l'onglet d'origine (réunion) si présent.
    const back = _consumeMeetingSupportBackHash();
    if (back) window.location.hash = back;
  }
}

async function _wizardFinalize(state: WizardState): Promise<void> {
  closeModal();
  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  overlay.id = 'modal-overlay';
  overlay.style.cssText = 'align-items:center;justify-content:center';

  overlay.innerHTML = `
    <div class="modal" style="padding:32px;text-align:center;max-width:480px">
      <h3 style="margin-bottom:12px">${esc(t('meetingSupport.finalize.title'))}</h3>
      <p style="color:var(--text3);font-size:13px;margin-bottom:16px">
        ${esc(t('meetingSupport.finalize.detail'))}
      </p>
      <div style="display:flex;justify-content:center"><span class="ms-spinner ms-spinner-lg"></span></div>
    </div>`;
  document.body.appendChild(overlay);

  const payload = {
    program_id: state.programId ? parseInt(state.programId) : null,
    project_id: state.projectId ? parseInt(state.projectId) : null,
    structure_id: state.structureId ? parseInt(state.structureId) : null,
    committee_id: parseInt(state.committeeId),
    with_risks: state.withRisks,
    fmt: state.fmt,
    base_url: window.location.origin,
    program_matrix: state.programMatrix,
    project_matrices: state.projects.map((p) => ({
      project_id: p.projectId,
      skipped: p.skipped,
      pngB64: p.pngB64,
      risksPngB64: p.risksPngB64 ?? null,
      filters: p.filters,
    })),
  };

  try {
    await api.meetingSupportFinalize(payload, 'support.' + state.fmt);
    closeModal();
    toast(t('meetingSupport.toast.success'));
    // Retour à l'onglet "Préparer un support" de la réunion d'origine si
    // le wizard a été lancé depuis là (cf. page-meeting.ts).
    const back = _consumeMeetingSupportBackHash();
    if (back) window.location.hash = back;
  } catch (err: unknown) {
    closeModal();
    const msg = err instanceof Error ? err.message : String(err);
    toast(t('meetingSupport.toast.error', { msg }), 'error');
  }
}

(function _ensureMsSpinnerCss() {
  if (document.getElementById('ms-spinner-css')) return;
  const style = document.createElement('style');
  style.id = 'ms-spinner-css';
  style.textContent = `
    @keyframes ms-spin { to { transform: rotate(360deg); } }
    .ms-spinner {
      display: inline-block;
      width: 14px; height: 14px;
      border: 2px solid var(--border2);
      border-top-color: var(--accent);
      border-radius: 50%;
      animation: ms-spin 0.7s linear infinite;
      vertical-align: middle;
      margin-right: 8px;
    }
    .ms-spinner-lg {
      width: 32px; height: 32px;
      border-width: 3px;
    }
  `;
  document.head.appendChild(style);
})();

// ── ESM globals shim (lot B.8 + S5.3) ──
window._wizardStep1 = _wizardStep1;
window._rewireStep1 = _rewireStep1;
window._wizardStep2Program = _wizardStep2Program;
window._wizardStep3Project = _wizardStep3Project;
window._captureMatrixFromIframe = _captureMatrixFromIframe;
window._openWizardStepModal = _openWizardStepModal;
window._confirmCancelWizard = _confirmCancelWizard;
window._wizardFinalize = _wizardFinalize;

export {};
